// src/screens/Assumptions.tsx
// ─────────────────────────────────────────────────────────────────────────────
// Sidebar Data Entry Tool — Assumptions
// Growth rates, inflation rate — feeds all projected-at-target-age calculations.
// ─────────────────────────────────────────────────────────────────────────────

import React from 'react';
import { ClientData } from '../types/ClientData';

interface Props {
  data: ClientData;
  onUpdate: (updates: Partial<ClientData>) => void;
}

function PctInput({ label, value, onChange, note }: {
  label: string; value: number; onChange: (v: number) => void; note?: string;
}) {
  const display = (value * 100).toFixed(1);
  return (
    <div>
      <label className="text-xs text-gray-400 uppercase tracking-wide font-medium">{label}</label>
      <div className="flex items-center mt-1 bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 focus-within:ring-2 focus-within:ring-ma-navy/20 focus-within:border-ma-navy/40">
        <input
          type="number" step="0.1" min="0" max="20"
          value={display}
          onChange={e => {
            const pct = parseFloat(e.target.value);
            if (!isNaN(pct)) onChange(pct / 100);
          }}
          className="flex-1 bg-transparent text-lg font-bold text-ma-navy outline-none"
        />
        <span className="text-gray-400 text-sm ml-2">%</span>
      </div>
      {note && <p className="text-xs text-gray-400 mt-0.5">{note}</p>}
    </div>
  );
}

export default function Assumptions({ data, onUpdate }: Props) {
  return (
    <div className="p-6 max-w-2xl mx-auto">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-ma-navy">Assumptions</h2>
        <p className="text-gray-500 mt-1 text-sm">
          Growth rates and inflation used for all projections. Change these to see how assumptions affect the plan.
        </p>
      </div>

      <div className="bg-white rounded-2xl border border-gray-200 p-5 space-y-5">
        <PctInput
          label="Pension growth rate"
          value={data.assumptionPensionGrowth}
          onChange={v => onUpdate({ assumptionPensionGrowth: v })}
          note="Annual growth assumed for pension investments"
        />
        <PctInput
          label="ISA / investment growth rate"
          value={data.assumptionIsaGrowth}
          onChange={v => onUpdate({ assumptionIsaGrowth: v })}
          note="Annual growth assumed for ISA / General Investment Account (GIA) investments"
        />
        <PctInput
          label="Cash / savings rate"
          value={data.assumptionCashRate}
          onChange={v => onUpdate({ assumptionCashRate: v })}
          note="Annual interest rate on cash savings"
        />
        <PctInput
          label="Property growth rate"
          value={data.assumptionPropertyGrowth}
          onChange={v => onUpdate({ assumptionPropertyGrowth: v })}
          note="Annual property appreciation"
        />

        <div className="border-t border-gray-200 pt-4">
          <PctInput
            label="Inflation rate"
            value={data.assumptionInflation}
            onChange={v => onUpdate({ assumptionInflation: v })}
            note="Used to convert future values to today's money"
          />
        </div>
      </div>

      <div className="mt-4 bg-blue-50 border border-blue-200 rounded-xl p-3">
        <p className="text-xs text-blue-700">
          These rates feed into pension, ISA, cash, and property projections across all screens.
          Changing a rate here will immediately update projected figures throughout the app.
        </p>
      </div>
    </div>
  );
}
